// API Keys feature logic — framework-agnostic, zero React imports.

import type { APIKey } from '@core/types/apikey';
import type { APIKeyStatus } from '@core/types/apikey';

// ============================================
// Status display map
// ============================================

export const apiKeyStatusMap: Record<string, { labelKey: string; variant: 'success' | 'error' | 'warning' | 'default' }> = {
  API_KEY_STATUS_UNSPECIFIED: { labelKey: 'apikeys.statusUnknown', variant: 'default' },
  API_KEY_STATUS_ACTIVE: { labelKey: 'apikeys.statusActive', variant: 'success' },
  API_KEY_STATUS_REVOKED: { labelKey: 'apikeys.statusRevoked', variant: 'error' },
  API_KEY_STATUS_EXPIRED: { labelKey: 'apikeys.statusExpired', variant: 'default' },
  API_KEY_STATUS_SUSPENDED: { labelKey: 'apikeys.statusSuspended', variant: 'warning' },
};

export function getStatusDisplay(status?: APIKeyStatus | string): { labelKey: string; variant: 'success' | 'error' | 'warning' | 'default' } {
  return apiKeyStatusMap[status ?? 'API_KEY_STATUS_UNSPECIFIED'] ?? apiKeyStatusMap.API_KEY_STATUS_UNSPECIFIED;
}

// ============================================
// Validation
// ============================================

export interface CreateKeyFormState {
  name: string;
  description: string;
  permissions: Record<string, string[]>;
  neverExpires: boolean;
  expiresAt: string; // ISO date string or empty
}

export const EMPTY_CREATE_FORM: CreateKeyFormState = {
  name: '',
  description: '',
  permissions: {},
  neverExpires: true,
  expiresAt: '',
};

export function validateCreateForm(data: CreateKeyFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) {
    errors.name = 'apikeys.validation.nameRequired';
  } else if (data.name.length > 100) {
    errors.name = 'apikeys.validation.nameTooLong';
  }
  if (data.description.length > 500) {
    errors.description = 'apikeys.validation.descriptionTooLong';
  }
  const hasPermissions = Object.values(data.permissions).some((a) => a.length > 0);
  if (!hasPermissions) {
    errors.permissions = 'apikeys.validation.permissionsRequired';
  }
  if (!data.neverExpires && !data.expiresAt) {
    errors.expiresAt = 'apikeys.validation.expirationRequired';
  }
  if (!data.neverExpires && data.expiresAt) {
    const d = new Date(data.expiresAt);
    if (isNaN(d.getTime()) || d <= new Date()) {
      errors.expiresAt = 'apikeys.validation.expirationFuture';
    }
  }
  return errors;
}

// ============================================
// Formatters
// ============================================

export function formatKeyPrefix(prefix: string): string {
  return prefix ? `${prefix}...` : '---';
}

export function formatDate(iso?: string): string {
  if (!iso) return '---';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '---';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(iso?: string): string {
  if (!iso) return '---';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '---';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatExpiration(key: APIKey): string {
  if (key.never_expires) return 'apikeys.neverExpires';
  return formatDate(key.expires_at);
}

// ============================================
// Business rules
// ============================================

export function canRevoke(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_ACTIVE' || key.status === 'API_KEY_STATUS_SUSPENDED';
}

export function canDelete(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_REVOKED' || key.status === 'API_KEY_STATUS_EXPIRED';
}

export function canSuspend(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_ACTIVE';
}

export function canActivate(key: APIKey): boolean {
  return key.status === 'API_KEY_STATUS_SUSPENDED';
}

export function isKeyExpired(key: APIKey): boolean {
  if (key.never_expires) return false;
  if (!key.expires_at) return false;
  const exp = new Date(key.expires_at);
  return exp <= new Date();
}
